<template>
  <div class="data-table">

    <div class="data-table__desktop">
      <table class="data-table__table">
        <thead class="data-table__head">
          <tr>
            <th
              v-for="(col, idx) in headers"
              :key="idx"
              scope="col"
              class="data-table__th"
              :class="[
                typeof col === 'object' ? col.class : undefined,
                typeof col === 'object' && col.align ? `data-table__th--${col.align}` : undefined
              ]"
            >{{ typeof col === 'object' ? col.label : col }}</th>
          </tr>
        </thead>

        <tbody v-if="!loading" class="data-table__body">
          <tr>
            <td :colspan="emptyColspan" class="data-table__cell-state">
              <div class="state-loading">
                <MyLoading :viewLoader="loading" type="" />
              </div>
            </td>
          </tr>
        </tbody>

        <tbody v-else-if="loading && items.length === 0" class="data-table__body">
          <tr>
            <td :colspan="emptyColspan" class="data-table__cell-state data-table__cell-state--empty">
              <span class="text-gray font-regular text-sm">{{ emptyMessage }}</span>
            </td>
          </tr>
        </tbody>

        <tbody v-else class="data-table__body">
          <tr
            v-for="(item, index) in items"
            :key="item[rowKey] ?? index"
            :class="{ 'data-table__row--odd': index % 2 !== 0 }"
          >
            <td
              v-for="(col, colIdx) in columns"
              :key="colIdx"
              class="data-table__td"
              :class="[col.class, col.align ? `data-table__td--${col.align}` : undefined]"
            >
              <component
                :is="col.component"
                v-if="col.component"
                v-bind="resolveProps(col, item)"
                v-on="resolveListeners(col, item)"
              />
              <template v-else>{{ col.format ? col.format(item[col.key], item) : item[col.key] }}</template>
            </td>
          </tr>
        </tbody>

        <tfoot v-if="footers.length > 0" class="data-table__foot">
          <tr>
            <td
              v-for="(foot, idx) in footers"
              :key="idx"
              class="data-table__td"
              :class="typeof foot === 'object' ? foot.class : undefined"
            >{{ typeof foot === 'object' ? foot.label : foot }}</td>
          </tr>
        </tfoot>
      </table>
    </div>

    <div class="data-table__mobile">
      <div v-if="!loading" class="state-loading">
        <MyLoading :viewLoader="loading" type="" />
      </div>

      <div
        v-else-if="loading && items.length === 0"
        class="state-center text-gray font-regular text-sm"
      >
        <span>{{ emptyMessage }}</span>
      </div>

      <template v-else-if="mobileComponent">
        <component
          v-for="(item, index) in items"
          :key="item[rowKey] ?? index"
          :is="mobileComponent"
          v-bind="resolveMobileProps(item)"
        />
      </template>
    </div>

    <StatusAlert v-if="viewError" status="error" role="alert">
      <template #body>
        <slot name="error">
          <p>{{ errorMessage || emptyMessage }}</p>
        </slot>
      </template>
    </StatusAlert>

    <Paginator
      v-if="totalPage > 1"
      :total-page="totalPage"
      :current-page="currentPage"
      :page-size="pageSize"
      @change-page="(page) => $emit('change-page', page)"
      @prev-page="$emit('prev-page')"
      @next-page="$emit('next-page')"
    />

  </div>
</template>

<script setup>
import MyLoading from "@/components/MyLoading.vue"
import StatusAlert from "@/components/Primitives/Alerts/StatusAlert.vue"
import Paginator from "@/components/Paginator.vue"

// Convenzione invertita: loading === false significa "in caricamento", loading === true significa "dati pronti".
// Allineata alla convenzione globale di MyLoading e viewLoader nel progetto.
const props = defineProps({
  /** Array degli elementi da renderizzare nelle righe */
  items: {
    type: Array,
    default: () => []
  },
  /**
   * Stato di caricamento — convenzione INVERTITA:
   * false = in caricamento (loader visibile), true = dati pronti (tabella visibile).
   */
  loading: {
    type: Boolean,
    default: false
  },
  /** Numero totale di pagine per il Paginator */
  totalPage: {
    type: Number,
    default: 0
  },
  /** Pagina correntemente attiva */
  currentPage: {
    type: Number,
    default: 1
  },
  /** Numero di elementi per pagina */
  pageSize: {
    type: Number,
    default: 10
  },
  /** Numero di colonne da attraversare per le righe di stato (loader, empty, error) */
  emptyColspan: {
    type: Number,
    default: 1
  },
  /** Testo mostrato nello stato vuoto e nel fallback della riga errore */
  emptyMessage: {
    type: String,
    default: ""
  },
  /** Flag per mostrare lo StatusAlert di errore */
  viewError: {
    type: Boolean,
    default: false
  },
  /**
   * Chiave unica per ogni item, usata come :key nel v-for.
   * Se l'item non ha la chiave specificata, viene usato l'indice come fallback.
   */
  rowKey: {
    type: String,
    default: "id"
  },
  /**
   * Intestazioni colonne.
   * Ogni elemento è una stringa oppure { label: String, class?: String, align?: 'left'|'center'|'right' }.
   * Il campo `align` applica la classe modificatrice BEM data-table__th--{align} sul <th>.
   */
  headers: {
    type: Array,
    default: () => []
  },
  /**
   * Piè di tabella — se l'array non è vuoto aggiunge un <tfoot> con una riga di <td>.
   * Ogni elemento è una stringa oppure { label: String, class?: String }.
   */
  footers: {
    type: Array,
    default: () => []
  },
  /**
   * Definizione colonne.
   * Ogni elemento è un oggetto con i seguenti campi opzionali:
   *   key: String              — accede a item[key]
   *   format: Function         — format(value, item) → stringa; usata se component non presente
   *   component: Component     — componente Vue da renderizzare nella cella
   *   props: Object|Function   — props statiche o funzione (item) => props
   *   class: String            — classe CSS aggiuntiva sul <td>
   *   align: 'left'|'center'|'right' — allineamento testo cella; applica data-table__td--{align}
   *   on: Object|Function      — listener eventi: { eventName: handler } oppure (item) => { eventName: handler }
   */
  columns: {
    type: Array,
    default: () => []
  },
  /** Componente da usare per ogni riga nella vista mobile. Riceve le props da mobileProps. */
  mobileComponent: {
    type: [Object, Function],
    default: null
  },
  /** Props statiche o funzione (item) => props da passare a mobileComponent. */
  mobileProps: {
    type: [Object, Function],
    default: () => ({})
  },
  /**
   * Testo mostrato nel body dell'alert di errore. Fallback a emptyMessage se vuoto.
   * Per contenuto ricco usa lo slot #error invece di questa prop.
   */
  errorMessage: {
    type: String,
    default: ""
  }
})

defineEmits(["change-page", "prev-page", "next-page"])

/**
 * Risolve le props da passare al component dinamico di una colonna.
 * @param {{ props?: Object|Function }} col
 * @param {Object} item
 * @returns {Object}
 */
function resolveProps(col, item) {
  if (!col.props) return {}
  return typeof col.props === "function" ? col.props(item) : col.props
}

/**
 * Risolve i listener da passare al component dinamico di una colonna.
 * @param {{ on?: Object|Function }} col
 * @param {Object} item
 * @returns {Object}
 */
function resolveListeners(col, item) {
  if (!col.on) return {}
  return typeof col.on === 'function' ? col.on(item) : col.on
}

/**
 * Risolve le props da passare a mobileComponent per un dato item.
 * @param {Object} item
 * @returns {Object}
 */
function resolveMobileProps(item) {
  if (!props.mobileProps) return {}
  return typeof props.mobileProps === "function" ? props.mobileProps(item) : props.mobileProps
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables.scss";

.data-table {
  width: 100%;
}

.data-table__table {
  width: 100%;
  border-collapse: collapse;
}

.data-table__head {
  background-color: $white;
}

:deep(.data-table__th) {
  padding: $spacingCellLg;
  color: $blu;
  font-family: MontserratSemiBold;
  font-size: $fontMiddle;
  text-align: left;
  border-bottom: 1px solid $blu;
}

:deep(.data-table__td) {
  padding: $spacingCellLg;
  color: $bluFive;
  font-family: OpenSansRegular;
  font-size: $fontNormal;
  line-height: 1.5;
  word-break: break-word;
  vertical-align: middle;
}

:deep(.data-table__row--odd td),
:deep(.data-table__row--odd .data-table__td) {
  background-color: $gray;
}

.data-table__cell-state {
  padding: $spacingCellLg;
  text-align: center;
}

.data-table__cell-state--empty {
  padding-top: calc(#{$spacingCellLg} * 2);
  padding-bottom: calc(#{$spacingCellLg} * 2);
}

.data-table__desktop { display: block; }
.data-table__mobile  { display: none; }

:global(body.mobile) .data-table__desktop { display: none; }
:global(body.mobile) .data-table__mobile  { display: block; }

:deep(.data-table__th--left)   { text-align: left; }
:deep(.data-table__th--center) { text-align: center; }
:deep(.data-table__th--right)  { text-align: right; }
:deep(.data-table__td--left)   { text-align: left; }
:deep(.data-table__td--center) { text-align: center; }
:deep(.data-table__td--right)  { text-align: right; }
</style>